import { HttpErrorResponse } from "@angular/common/http";
import { ErrorHandler, inject, Injectable, Injector } from "@angular/core";
import { BiTranslateService } from "@globals/bi-translate";
import { isRunningInIE } from "@globals/helper-functions";
import { BiHttpErrorResponse } from "@globals/models/BiHttpErrorResponse";
import { BiApplicationInsightsService } from "./bi-application-insights.service";
import { BiDialogBaseService } from "./bi-dialog-base.service";
import { BiNotificationConfig, BiToastNotificationService, NotificationType } from "./bi-toast-notification.service";
import { APPLICATION_NAME } from "./injection-tokens/application-name-token";
import { LogBaseService } from "./log-base.service";

/**
 * Custom, global error handler for Angular that we use to log front end errors!
 */
@Injectable()
export class BiErrorHandlerService implements ErrorHandler {
  /**
   * We need the Angular Injector as some dependencies in this service would cause cyclic dependency if injected via inject/ctor
   * (e.i. auto inject by Angular DI) - they must manually be injected when needed!
   * Info here: https://medium.com/@amcdnl/global-error-handling-with-angular2-6b992bdfb59c
   */
  private readonly injector = inject(Injector);

  private readonly translator = inject(BiTranslateService);
  private readonly appInsightsService = inject(BiApplicationInsightsService);
  private readonly logger = inject(LogBaseService);

  private readonly appName = inject(APPLICATION_NAME);

  constructor() {
    console.log("Current application name is: ", this.appName);
  }

  public handleError(error: Error): void {
    if (!this.checkAndHandleChunkLoadError(error.message)) return;

    // application insights
    this.appInsightsService.logException(error);

    // If HttpErrorResponse, it's a server error and we handle that differently
    if (error instanceof HttpErrorResponse) this.handleServerResponse(error as BiHttpErrorResponse);
    else this.handleClientError(error);
  }

  /**
   * Helper for handling server response during development
   */
  private handleServerResponse(response: BiHttpErrorResponse) {
    console.error("SERVER RESPONSE: ", response);
    // if unauthorized, access token will be refreshed so don't do anything.
    // If the error was due to a refresh token issue, user is redirected to login where a message should be shown
    if (response.refreshTokenInvalidOrExpired || response.status === 401 || (response.status === 400 && response.error?.errorMessage?.indexOf && response.error.errorMessage.indexOf("token") !== -1))
      return;

    // Some other server error occured. Show to user...
    const notifier = this.injector.get(BiToastNotificationService);
    // If we have log id, this must be included in message
    const errorMessage = response.error.logId
      ? this.translator.instant("errorMessages.GeneralErrorWithLogId", {
          logId: response.error.logId
        })
      : this.translator.instant("errorMessages.ErrorOccurred");

    const notificationConfig = new BiNotificationConfig(NotificationType.WARNING, this.translator.instant("errorMessages.Error"), errorMessage);

    notificationConfig.sticky = true;
    notifier.createNotification(notificationConfig);
  }

  /**
   * Helper for handling client/browser errors. Unlike server errors, these errors are also logged in backend.
   */
  private handleClientError(err: TypeError | ReferenceError) {
    // As of Angular 9, unsafe resource usage in DOM always throws an errror even though we use ByPassSecurityTrust... methods so don't
    // anything if the error is one like that
    if (err.message && err.message.indexOf("unsafe value used in a resource URL") !== -1) return;
    if (err.message && err.message.indexOf("GEOCODER_GEOCODE") !== -1) return;

    if (err.message && err.message.indexOf("TrackerStorageType is not defined") !== -1) return;

    console.error("CLIENT ERROR: ", err);

    if ((err.message && !isRunningInIE()) || (err.stack && !(err instanceof TypeError))) {
      if (!err.message.includes("Refresh access token failed")) {
        const notifier = this.injector.get(BiToastNotificationService);
        const notificationConfig = new BiNotificationConfig(NotificationType.ERROR, this.translator.instant("errorMessages.ErrorOccurred") + ":" + err.message, undefined);
        notificationConfig.timeout = 4000;
        notifier.createNotification(notificationConfig);
      }

      this.logger.createLog(err, this.appName).subscribe();
    }
  }

  private checkAndHandleChunkLoadError(errorMessage: string) {
    if (/Loading chunk [\d]+ failed/.test(errorMessage)) {
      const dialogService = this.injector.get(BiDialogBaseService);

      let imageHtml = '<div class="text-center mt-4"><img src="/StaticFiles/images/Reload/Windows.png"/></div>';
      let dialogText = this.translator.instant("app.NewUpdateText");

      let acceptVisible = false;

      if (navigator.userAgent) {
        const isIOS = navigator.userAgent.indexOf("iPhone") !== -1 || navigator.userAgent.indexOf("iPad") !== -1;
        const isMac = !isIOS && navigator.userAgent.indexOf("Mac") !== -1;
        const isWindows = navigator.userAgent.indexOf("Windows") !== -1;
        const isAndroid = navigator.userAgent.indexOf("Android") !== -1;

        if (isMac) {
          const isSafari = navigator.userAgent.indexOf("Safari") !== -1 && navigator.userAgent.indexOf("Chrome") === -1;

          if (isSafari) {
            imageHtml = '<div class="text-center mt-4"><img src="/StaticFiles/images/Reload/Mac_Safari.png"/></div>';
          }

          const isFirefox = navigator.userAgent.indexOf("Firefox") !== -1;
          const isChrome = navigator.userAgent.indexOf("Chrome") !== -1;

          if (isFirefox || isChrome) {
            imageHtml = '<div class="text-center mt-4"><img src="/StaticFiles/images/Reload/Mac_Chrome_Firefox.png"/></div>';
          }
        } else if (!isWindows) {
          if (isAndroid || isIOS) {
            acceptVisible = true;
            dialogText = "";
            imageHtml = `<p class="mt-4">${this.translator.instant("app.NewUpdateMobileBrowser")}</p>`;
          }
        }
      }

      dialogService.openConfirm(
        this.translator.instant("app.NewUpdate"),
        () => (window.location as any).reload(true),
        () => (window.location as any).reload(true),
        dialogText + imageHtml,
        {
          acceptKey: "shared.OK",
          rejectKey: "shared.Reject"
        },
        undefined,
        acceptVisible,
        false
      );
      return false;
    }

    return true;
  }
}
